import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { store, persistor } from './store';
import AppRoutes from './routes/AppRoutes';
import ErrorBoundary from './components/common/ErrorBoundary';
import { useAppSelector, useAppDispatch } from './store/hooks';
import { setModules } from './store/slices/moduleSlice';

// Create theme for Material-UI
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#002060',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.12)',
            },
          },
        },
      },
    },
  },
});

// Component that handles module data initialization
const AppContent: React.FC = () => {
  const { isAuthenticated, moduleDetails } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  // Initialize modules from auth state if available
  useEffect(() => {
    if (isAuthenticated && moduleDetails && moduleDetails.length > 0) {
      // console.log('Initializing modules from persisted state:', moduleDetails);
      dispatch(setModules(moduleDetails));
    }
  }, [isAuthenticated, moduleDetails, dispatch]);

  // Show loading state while rehydrating
  return (
    <ErrorBoundary>
      <AppRoutes />
    </ErrorBoundary>
  );
};

// Loading component for PersistGate
const LoadingSpinner = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh'
    }}>
      <div>Loading application...</div>
    </div>
  </ThemeProvider>
);

// Main App component
const App: React.FC = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={<LoadingSpinner />} persistor={persistor}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <AppContent />
          </Router>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
